import { Link } from "react-router-dom";
import { PHONE_DISPLAY, EMAIL, ADDRESS, REGISTERED_ADDRESS, SIRET, LEGAL_FORM, waLink } from "@/data/site";
import { COACHING_NAME } from "@/data/coachingBrand";

const COLS = [
  {
    title: "Alsace",
    path: "/alsace",
    links: [
      { label: "Mulhouse", path: "/mulhouse" },
      { label: "Colmar", path: "/colmar" },
      { label: "Strasbourg", path: "/strasbourg" },
      { label: "Saint-Louis", path: "/saint-louis" },
    ],
  },
  {
    title: "Côte d'Azur",
    path: "/cote-d-azur",
    links: [
      { label: "Nice", path: "/nice" },
      { label: "Cannes", path: "/cannes" },
      { label: "Antibes", path: "/antibes" },
      { label: "Saint-Tropez", path: "/saint-tropez" },
      { label: "Monaco", path: "/cote-d-azur#monaco" },
    ],
  },
  {
    title: "Marrakech",
    path: "/marrakech",
    links: [
      { label: "Villas", path: "/marrakech#villas" },
      { label: "Riads", path: "/marrakech#riads" },
      { label: "Appartements", path: "/marrakech#appartements" },
      { label: "Conciergerie", path: "/marrakech#conciergerie" },
    ],
  },
  {
    title: "Services",
    path: "/proprietaires",
    links: [
      { label: "Sous-location professionnelle", path: "/sous-location" },
      { label: "Conciergerie immobilière", path: "/conciergerie" },
      { label: "Espace propriétaires", path: "/proprietaires" },
      { label: `${COACHING_NAME} · Conciergerie`, path: "/formation-conciergerie" },
      { label: `${COACHING_NAME} · Sous-location`, path: "/formation-sous-location-professionnelle" },
      { label: "Accompagnement propriétaires", path: "/accompagnement-location-courte-duree" },
      { label: "Nos biens", path: "/nos-biens" },
      { label: "Conseils immobiliers", path: "/conseils" },
      { label: "FAQ", path: "/faq" },
      { label: "Ismail Ozcan", path: "/a-propos" },
    ],
  },
];

const LEGAL = [
  { label: "Mentions légales", path: "/mentions-legales" },
  { label: "Politique de confidentialité", path: "/politique-de-confidentialite" },
  { label: "CGV", path: "/cgv" },
  { label: "Cookies", path: "/cookies" },
];

export default function Footer() {
  return (
    <footer data-testid="main-footer" className="bg-[#0a0a0a] border-t border-[#1f1f1f]">
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Link to="/" data-testid="footer-logo" className="font-heading text-3xl font-light tracking-[0.08em] text-[#f5f3ef]">
              OZC <span className="text-[#c9a96e]">IMMO</span>
            </Link>
            <p className="mt-4 text-sm text-[#8a8a8a] leading-relaxed max-w-xs">
              Conciergerie, sous-location professionnelle et accompagnement individuel.
            </p>
            <p className="mt-2 text-xs italic text-[#5c5c5c]">Votre bien. Notre expertise. Votre tranquillité.</p>
            <div className="mt-8">
              <p className="eyebrow mb-4">Contact</p>
              <ul className="space-y-2 text-sm text-[#b8b5ae]">
                <li><a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-phone" className="hover:text-[#c9a96e] transition-colors">{PHONE_DISPLAY} <span className="text-[#25D366]">· WhatsApp</span></a></li>
                <li className="text-xs text-[#737373]">Contactez-nous de préférence sur WhatsApp — réponse rapide.</li>
                <li><a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="hover:text-[#c9a96e] transition-colors">Parler directement à Ismail sur WhatsApp</a></li>
                <li><a href={`mailto:${EMAIL}`} data-testid="footer-email" className="hover:text-[#c9a96e] transition-colors">{EMAIL}</a></li>
                <li data-testid="footer-contact-address" className="text-[#a3a3a3]">Adresse de contact : {ADDRESS.street}, {ADDRESS.postalCode} {ADDRESS.city}, {ADDRESS.country}</li>
                <li data-testid="footer-registered-address" className="text-[#a3a3a3]">Siège social : {REGISTERED_ADDRESS.street}, {REGISTERED_ADDRESS.postalCode} {REGISTERED_ADDRESS.city}</li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {COLS.map((col) => (
              <div key={col.title}>
                <Link to={col.path} data-testid={`footer-region-${col.path.slice(1)}`} className="eyebrow block mb-5 hover:text-[#f5f3ef] transition-colors">{col.title}</Link>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.path}>
                      <Link to={l.path} data-testid={`footer-link-${l.path.replace(/[/#]/g, "-").slice(1)}`} className="text-sm text-[#8a8a8a] hover:text-[#f5f3ef] transition-colors">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-[#1f1f1f]">
        <div className="max-w-7xl mx-auto px-5 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p data-testid="footer-legal-identity" className="text-[11px] text-[#a3a3a3]">
            &copy; {new Date().getFullYear()} OZC IMMO — {LEGAL_FORM} — SIRET {SIRET}
          </p>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            <li><Link to="/mentions-legales#photographies" data-testid="footer-photo-information" className="text-[11px] text-[#a3a3a3] hover:text-[#c9a96e] transition-colors">Photographies d’inspiration · crédits</Link></li>
            {LEGAL.map((l) => (
              <li key={l.path}>
                <Link to={l.path} data-testid={`footer-legal-${l.path.slice(1)}`} className="text-[11px] text-[#5c5c5c] hover:text-[#c9a96e] transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
